<script>
import Vue from 'vue'
import InputLine from '@/components/atoms/inputlib/components/InputLine'
import FormValidation from '@/components/atoms/FormValidation'
import InputCheckbox from '@/components/atoms/inputlib/components/InputCheckbox'
import ButtonLoader from '@/components/atoms/ButtonLoader'
import InputPhone from '@/components/atoms/inputlib/components/InputPhone'
import auth from '@/modules/mixins/auth'

// Accept-endpoint field -> form model key; the form keeps the legacy snake_case model of the
// auth mixin, the accept endpoint (core-api) speaks camelCase (tasks#79)
const INVITE_FIELDS = {
    firstName: 'first_name',
    lastName: 'last_name',
    phone: 'phone',
    plainPassword: 'plain_password',
}

export default {
    name: 'SignUp',
    components: {
        InputPhone,
        ButtonLoader,
        InputCheckbox,
        FormValidation,
        InputLine,
    },
    mixins: [auth],
    data() {
        return {
            invite: null,
            inviteError: false,
            bannerError: null,
        }
    },
    computed: {
        facebookEnabled() {
            return !this.config.facebook_disabled
        },
        inviteToken() {
            return this.$route.query.invite || null
        },
        inviteMode() {
            return Boolean(this.inviteToken)
        },
        // the loaded invite belongs to the token in the URL (query-only navigation reuses the view)
        inviteReady() {
            return Boolean(this.invite && this.invite.token === this.inviteToken)
        },
    },
    watch: {
        inviteToken: {
            immediate: true,
            handler(token) {
                this.invite = null
                this.inviteError = false
                this.bannerError = null
                this.$store.dispatch('site/resetViolations')
                // the prefilled invitee details must not survive a token change / the plain form
                this.$set(this.model, 'first_name', '')
                this.$set(this.model, 'last_name', '')
                this.$set(this.model, 'phone', '')
                if (token) this.loadInvite(token)
            },
        },
    },
    async beforeRouteEnter(to, from, next) {
        const user = Vue.prototype.$user
        if (!to.query.invite) {
            if (user.isLoggedIn) next({ name: 'restaurant-list' })
            else next()
            return
        }
        // an invite is for a specific e-mail: drop any existing session first (Sso.vue pattern);
        // logout() can reject inside its store clean-up, reset() still clears the tokens
        if (user.isLoggedIn) {
            try {
                await user.logout(false)
            } catch (e) {
                user.reset()
            }
        }
        next()
    },
    methods: {
        facebook() {
            if (this.facebookEnabled) this.$user.facebookAuth()
        },
        async loadInvite(token) {
            let invite
            try {
                invite = await this.$user.getInvite(token)
            } catch (e) {
                invite = undefined
            }
            if (token !== this.inviteToken) return // obsolete response, the token changed meanwhile
            if (!invite) {
                this.inviteError = true
                return
            }
            this.$set(this.model, 'first_name', invite.firstName || '')
            this.$set(this.model, 'last_name', invite.lastName || '')
            this.$set(this.model, 'phone', invite.phone || '')
            this.invite = { ...invite, token }
        },
        toAcceptPayload() {
            return {
                token: this.inviteToken,
                firstName: this.model.first_name,
                lastName: this.model.last_name,
                phone: this.model.phone,
                plainPassword: this.model.plain_password,
                acceptedTermsVersion: this.model.accepted_terms_version,
                acceptedPrivacyVersion: this.model.accepted_privacy_version,
                isSubscribedForNewsletter: Boolean(this.model.is_subscribed_for_newsletter),
            }
        },
        // 400 { errors: { field: [msg, msg] } } -> field violations for FormValidation, the rest to the banner
        applyAcceptErrors(errors) {
            const violations = []
            const banner = []
            Object.keys(errors).forEach((key) => {
                const message = [].concat(errors[key]).join(' ')
                if (INVITE_FIELDS[key])
                    violations.push({ propertyPath: INVITE_FIELDS[key], message })
                else banner.push(message)
            })
            if (violations.length) this.$store.dispatch('site/setViolations', violations)
            // a non-field error (_, body, stale terms/privacy versions) needs a fresh page state
            if (banner.length)
                this.bannerError = `${banner.join(' ')} ${this.$t('login.invite.reload_hint')}`
        },
        async acceptInvite() {
            this.bannerError = null
            this.loading = true
            let res
            try {
                res = await this.$user.acceptInvite(this.toAcceptPayload())
            } catch (e) {
                this.loading = false
                if (e?.errors) this.applyAcceptErrors(e.errors)
                else this.bannerError = this.$t('login.invite.accept_failed')
                return Promise.reject(e)
            }
            if (!res?.refresh_token) {
                // the client resolves undefined on a network failure
                this.loading = false
                this.bannerError = this.$t('login.invite.accept_failed')
                return Promise.reject(res)
            }
            // the account exists from here on: sign in with the returned refresh token (Sso.vue path),
            // fall back to the login page if that fails
            try {
                if (this.$user.isLoggedIn) await this.$user.logout(false) // session restored meanwhile
                await this.$user.refreshToken(res.refresh_token)
                await this.$user.getMe()
                if (!this.$user.model?.id) throw new Error('profile not loaded')
                this.$router.push({ name: 'restaurant-list', params: { setLocation: true } })
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e)
                // getMe() flags the session as logged in even when the client swallowed a network
                // failure; LogIn's entry guard would bounce a "logged-in" user to the restaurants
                this.$user.reset()
                this.$router.push({ name: 'login' })
            } finally {
                this.loading = false
            }
        },
        async submitForm() {
            return this.inviteMode ? await this.acceptInvite() : await this.register()
        },
    },
}
</script>

<template lang="pug">
.container-fluid.container-lg
    .row.mb-11
        .col-12.col-lg-4.offset-lg-4
            template(v-if="inviteMode && inviteError")
                h1.mt-10.text-center.font-weight-bold {{$t('login.invite.title')}}
                .alert.alert-danger.d-flex.mt-6
                    icon.text-white.mr-4(icon="fa-solid fa-info-circle" size="lg")
                    | {{$t('login.invite.invalid')}}
                .text-center.font-weight-semibold
                    router-link(:to="{name: 'login'}") {{$t('login.log_in')}}
            template(v-else)
                h1.mt-10.text-center.font-weight-bold {{ inviteMode ? $t('login.invite.title') : $t('login.sign_up') }}
                // text interpolation on purpose: vuex-i18n does not escape placeholders, the company name is admin-entered data
                .mb-6.text-center.font-weight-semibold(v-if="inviteMode && inviteReady") {{ $t('login.invite.invited_by', {company: invite.companyName}) }}
                .mb-6.text-center.font-weight-semibold(v-else-if="!inviteMode") {{$t('login.existing_user')}}&nbsp;
                    router-link(:to="{name: 'login'}") {{$t('login.log_in')}}
                .alert.alert-danger.d-flex(v-if="bannerError")
                    icon.text-white.mr-4(icon="fa-solid fa-info-circle" size="lg")
                    | {{ bannerError }}
                //button.btn.btn-dark.btn-block(v-if="facebookEnabled" @click="facebook")
                //    icon(:icon="['fab', 'facebook']")
                //    span {{$t('login.continue_facebook')}}
                // keyed + rendered only once the invite is loaded: FormValidation collects its inputs
                // and their values at mount only (prefill before mount, remount on a mode switch)
                form-validation(v-if="!inviteMode || inviteReady" :key="inviteToken || 'signup'" :on-submit="submitForm")
                    .form-row
                        .col-12.col-lg-6
                            input-line(v-model="model.first_name" type="alpha" validate="first_name" required live) {{$t('labels.first_name')}}
                        .col-12.col-lg-6
                            input-line(v-model="model.last_name" type="alpha" validate="last_name" required live) {{$t('labels.last_name')}}
                    input-line(v-if="!inviteMode" v-model="model.email" type="email" validate="email" required live) {{$t('labels.email')}}
                    .form-group(v-else)
                        label {{$t('labels.email')}}
                        .font-weight-semibold {{ invite.email }}
                        small.text-muted {{$t('login.invite.email_hint')}}
                    input-phone(v-model="model.phone" type="phone" validate="phone" required live) {{$t('labels.phone')}}
                    input-line(type="password" v-model="model.plain_password" validate="plain_password" autocomplete="new-password" :min="8" :max="300" required same live) {{$t('labels.psw')}}
                    input-line(type="password" v-model="psw" :min="8" :max="300" :same="$t('labels.error.psw')" required live) {{$t('labels.re_psw')}}
                    input-line(v-if="!inviteMode" v-model="model.registration_code" validate="registration_code" type="text") {{$t('labels.registration_code')}}
                    input-checkbox.mb-1.mt-6.d-flex(v-model="acceptTerms" :content="$t('base.accept.terms', {link: terms})" required)
                    input-checkbox.mb-1.d-flex(v-model="acceptPrivacy" :content="$t('base.accept.privacy', {link: privacy})" required)
                    input-checkbox.d-flex(v-model="model.is_subscribed_for_newsletter" :content="$t('base.accept.newsletter')")
                    template(v-slot:submit)
                        button-loader.btn.btn-primary.btn-block.mt-6.mb-8(type="submit" :loading="loading" inline) {{ inviteMode ? $t('login.invite.accept') : $t('login.sign_up') }}
</template>
